import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { PhotoUpload } from './PhotoUpload';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Building2,
    Percent,
    FileText,
    Save,
    Loader2,
    Star,
    Award,
    TrendingUp,
    CheckCircle,
    Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const specializationOptions = [
    'Appartement',
    'Maison',
    'Terrain',
    'Commercial',
    'Luxe',
    'Neuf',
    'Ancien',
    'Location',
    'Investissement',
    'Viager',
];

export const AgentProfileEditor = () => {
    const { user, token, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        agency_name: '',
        city: '',
        commission_rate: '',
        bio: '',
        specializations: [],
    });

    useEffect(() => {
        if (user) {
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                phone: user.phone || '',
                agency_name: user.agency_name || '',
                city: user.city || '',
                commission_rate: user.commission_rate || '',
                bio: user.bio || '',
                specializations: user.specializations || [],
            });
        }
    }, [user]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleSpecialization = (spec) => {
        setFormData(prev => ({
            ...prev,
            specializations: prev.specializations.includes(spec)
                ? prev.specializations.filter(s => s !== spec)
                : [...prev.specializations, spec]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const updateData = {
                ...formData,
                commission_rate: formData.commission_rate ? parseFloat(formData.commission_rate) : null,
            };

            const response = await axios.put(`${API}/agents/profile`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            updateUser(response.data);
            toast.success('Profil mis à jour avec succès !');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoUpdated = (url) => {
        updateUser({ photo_url: url });
    };

    if (!user) return null;

    return (
        <div className="space-y-6" data-testid="agent-profile-editor">
            {/* Two-column layout on larger screens */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left Column - Photo & Stats */}
                <div className="space-y-6">
                    {/* Photo Upload */}
                    <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                        <h2 className="font-heading text-lg font-semibold text-scalimmo-navy mb-4 flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Photo de profil
                        </h2>
                        <PhotoUpload
                            currentPhotoUrl={user.photo_url}
                            onPhotoUpdated={handlePhotoUpdated}
                            token={token}
                        />
                    </div>

                    {/* Stats Card */}
                    <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                        <h2 className="font-heading text-lg font-semibold text-scalimmo-navy mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" />
                            Statistiques
                        </h2>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Note moyenne</span>
                                <div className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-scalimmo-gold fill-scalimmo-gold" />
                                    <span className="font-semibold">{(user.avg_rating || 0).toFixed(1)}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Avis clients</span>
                                <span className="font-semibold">{user.total_reviews || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Rendez-vous</span>
                                <span className="font-semibold">{user.total_appointments || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Boost visibilité</span>
                                <span className="font-semibold text-scalimmo-gold">+{user.visibility_boost || 0}</span>
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t">
                                <span className="text-sm text-muted-foreground">Statut</span>
                                {user.is_top_performer ? (
                                    <Badge className="elite-badge text-xs">
                                        <Award className="w-3 h-3" />
                                        Top Performer
                                    </Badge>
                                ) : (
                                    <Badge variant="secondary" className="text-xs">Standard</Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Preview Link */}
                    <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => window.open(`/agents/${user.id}`, '_blank')}
                        data-testid="preview-profile-btn"
                    >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir mon profil public
                    </Button>
                </div>

                {/* Right Column - Form */}
                <div className="lg:col-span-2">
                    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-border/60 p-6 shadow-soft space-y-6">
                        <h2 className="font-heading text-xl font-semibold text-scalimmo-navy flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Informations du profil
                        </h2>

                        {/* Personal Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Informations personnelles
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="first_name">Prénom</Label>
                                    <Input
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={(e) => handleChange('first_name', e.target.value)}
                                        data-testid="input-firstname"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="last_name">Nom</Label>
                                    <Input
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={(e) => handleChange('last_name', e.target.value)}
                                        data-testid="input-lastname"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Coordonnées
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="phone" className="flex items-center gap-1.5">
                                        <Phone className="w-4 h-4" />
                                        Téléphone
                                    </Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        placeholder="06 12 34 56 78"
                                        data-testid="input-phone"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="city" className="flex items-center gap-1.5">
                                        <MapPin className="w-4 h-4" />
                                        Ville
                                    </Label>
                                    <Input
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        placeholder="Paris"
                                        data-testid="input-city"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Professional Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Informations professionnelles
                            </h3>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="agency_name" className="flex items-center gap-1.5">
                                        <Building2 className="w-4 h-4" />
                                        Nom de l'agence
                                    </Label>
                                    <Input
                                        id="agency_name"
                                        value={formData.agency_name}
                                        onChange={(e) => handleChange('agency_name', e.target.value)}
                                        placeholder="Votre agence"
                                        data-testid="input-agency"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="commission_rate" className="flex items-center gap-1.5">
                                        <Percent className="w-4 h-4" />
                                        Honoraires (%)
                                    </Label>
                                    <Input
                                        id="commission_rate"
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="10"
                                        value={formData.commission_rate}
                                        onChange={(e) => handleChange('commission_rate', e.target.value)}
                                        placeholder="3.5"
                                        data-testid="input-commission"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Specializations */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Spécialités
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {specializationOptions.map(spec => (
                                    <Badge
                                        key={spec}
                                        variant={formData.specializations.includes(spec) ? 'default' : 'outline'}
                                        className={`cursor-pointer transition-all ${
                                            formData.specializations.includes(spec)
                                                ? 'bg-scalimmo-navy hover:bg-scalimmo-navy/90'
                                                : 'hover:bg-muted'
                                        }`}
                                        onClick={() => toggleSpecialization(spec)}
                                        data-testid={`spec-${spec}`}
                                    >
                                        {formData.specializations.includes(spec) && (
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                        )}
                                        {spec}
                                    </Badge>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Cliquez pour sélectionner vos spécialités
                            </p>
                        </div>

                        {/* Bio / Presentation */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                Présentation
                            </h3>
                            <div className="space-y-2">
                                <Label htmlFor="bio">
                                    Décrivez votre parcours et votre approche
                                </Label>
                                <Textarea
                                    id="bio"
                                    value={formData.bio}
                                    onChange={(e) => handleChange('bio', e.target.value)}
                                    placeholder="Présentez-vous aux vendeurs potentiels. Parlez de votre expérience, de vos réussites, de ce qui vous différencie..."
                                    rows={5}
                                    className="resize-none"
                                    data-testid="input-bio"
                                />
                                <p className="text-xs text-muted-foreground">
                                    {formData.bio.length}/500 caractères recommandés
                                </p>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-4 border-t">
                            <Button
                                type="submit"
                                className="w-full sm:w-auto btn-primary"
                                disabled={loading}
                                data-testid="save-profile-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Enregistrer les modifications
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
